import { memo } from "react";
import { Play, Pause, RotateCcw, Volume2 } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Button } from "@/components/ui/button";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { LyriaState, MusicConfig } from "@/lib/lyria-client";

interface TransportBarProps {
  lyriaState: LyriaState;
  config: MusicConfig;
  volume: number;
  onPlay: () => void;
  onPause: () => void;
  onResetContext: () => void;
  onVolumeChange: (v: number) => void;
  onUpdateConfig: (config: Partial<MusicConfig>, needsReset?: boolean) => void;
}

const BPM_PRESETS = [80, 100, 120, 128, 140, 160];

const NOTES = ["C", "Db", "D", "Eb", "E", "F", "Gb", "G", "Ab", "A", "Bb", "B"];

const SCALE_OPTIONS = [
  { scale: "C_MAJOR_A_MINOR", major: "C", minor: "A" },
  { scale: "D_FLAT_MAJOR_B_FLAT_MINOR", major: "Db", minor: "Bb" },
  { scale: "D_MAJOR_B_MINOR", major: "D", minor: "B" },
  { scale: "E_FLAT_MAJOR_C_MINOR", major: "Eb", minor: "C" },
  { scale: "E_MAJOR_D_FLAT_MINOR", major: "E", minor: "Db" },
  { scale: "F_MAJOR_D_MINOR", major: "F", minor: "D" },
  { scale: "G_FLAT_MAJOR_E_FLAT_MINOR", major: "Gb", minor: "Eb" },
  { scale: "G_MAJOR_E_MINOR", major: "G", minor: "E" },
  { scale: "A_FLAT_MAJOR_F_MINOR", major: "Ab", minor: "F" },
  { scale: "A_MAJOR_G_FLAT_MINOR", major: "A", minor: "Gb" },
  { scale: "B_FLAT_MAJOR_G_MINOR", major: "Bb", minor: "G" },
  { scale: "B_MAJOR_A_FLAT_MINOR", major: "B", minor: "Ab" },
] as const;

export const TransportBar = memo(function TransportBar({
  lyriaState,
  config,
  volume,
  onPlay,
  onPause,
  onResetContext,
  onVolumeChange,
  onUpdateConfig,
}: TransportBarProps) {
  const isActive = lyriaState === "playing" || lyriaState === "starting" || lyriaState === "buffering";
  const isConnected = lyriaState !== "disconnected" && lyriaState !== "connecting";

  const currentBpm = config.bpm ?? 120;
  const selectedScale =
    SCALE_OPTIONS.find((option) => option.scale === config.scale) ?? SCALE_OPTIONS.find((option) => option.scale === "C_MAJOR_A_MINOR")!;
  const currentNote = selectedScale.major;
  const displayScale = `${selectedScale.major} major / ${selectedScale.minor} minor`;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-40 grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 px-3 py-3 bg-black/90 backdrop-blur-xl border-t border-white/5 sm:flex sm:justify-between sm:px-6">
      {/* Left: Volume */}
      <div className="flex min-w-0 items-center gap-2 sm:w-56 sm:gap-3">
        <Volume2 className="w-4 h-4 text-muted-foreground shrink-0" />
        <Slider
          value={[volume * 100]}
          onValueChange={([v]) => onVolumeChange(v / 100)}
          max={100}
          step={1}
          className="w-16 sm:w-28"
        />
      </div>

      {/* Center: Transport Controls */}
      <div className="flex items-center gap-2 sm:gap-4">
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 rounded-lg hover:bg-white/5 text-muted-foreground hover:text-foreground"
          onClick={onResetContext}
          disabled={!isConnected}
          aria-label="Reset context"
        >
          <RotateCcw className="w-4 h-4" />
        </Button>

        <button
          onClick={isActive ? onPause : onPlay}
          aria-label={isActive ? "Pause" : "Play"}
          className={cn(
            "h-16 w-16 rounded-lg flex items-center justify-center transition-all",
            "hover:scale-105 active:scale-95",
            isActive
              ? "bg-white/15 text-foreground"
              : "text-white shadow-lg shadow-pink-500/20",
          )}
          style={
            !isActive
              ? { backgroundColor: "hsl(330 80% 70%)" }
              : undefined
          }
        >
          {isActive ? (
            <Pause className="w-7 h-7" />
          ) : (
            <Play className="w-7 h-7 ml-1" />
          )}
        </button>

        <div className="hidden w-10 sm:block" /> {/* Spacer to balance reset button */}
      </div>

      {/* Right: BPM and key */}
      <div className="flex min-w-0 items-center justify-end gap-2 sm:w-48">
        {/* BPM Popover */}
        <Popover>
          <PopoverTrigger asChild>
            <button className="px-2 py-1.5 rounded-lg text-[11px] font-mono bg-white/5 border border-white/10 text-muted-foreground hover:text-foreground hover:bg-white/10 transition-all sm:px-3 sm:text-xs">
              {currentBpm} BPM
            </button>
          </PopoverTrigger>
          <PopoverContent side="top" className="w-56 p-4 bg-black/95 border-white/10">
            <div className="space-y-3">
              <div className="text-xs text-muted-foreground uppercase tracking-wider">Tempo</div>
              <Slider
                value={[currentBpm]}
                onValueChange={([v]) => onUpdateConfig({ bpm: v }, true)}
                min={60}
                max={200}
                step={1}
              />
              <div className="text-center text-lg font-mono text-foreground">{currentBpm}</div>
              <div className="flex flex-wrap gap-1.5">
                {BPM_PRESETS.map((bpm) => (
                  <button
                    key={bpm}
                    onClick={() => onUpdateConfig({ bpm }, true)}
                    className={cn(
                      "px-2 py-1 rounded text-xs transition-all",
                      currentBpm === bpm
                        ? "bg-white/15 text-foreground"
                        : "bg-white/5 text-muted-foreground hover:bg-white/10"
                    )}
                  >
                    {bpm}
                  </button>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>

        {/* KEY Popover */}
        <Popover>
          <PopoverTrigger asChild>
            <button className="hidden px-3 py-1.5 rounded-lg text-xs font-mono bg-white/5 border border-white/10 text-muted-foreground hover:text-foreground hover:bg-white/10 transition-all sm:block">
              {displayScale}
            </button>
          </PopoverTrigger>
          <PopoverContent side="top" className="w-64 p-4 bg-black/95 border-white/10">
            <div className="space-y-3">
              <div className="text-xs text-muted-foreground uppercase tracking-wider">Key</div>
              <div className="rounded-lg bg-white/5 px-3 py-2 text-xs text-muted-foreground">
                Relative major/minor pair
              </div>
              {/* Note grid */}
              <div className="grid grid-cols-4 gap-1.5">
                {NOTES.map((note) => (
                  <button
                    key={note}
                    onClick={() => {
                      const nextScale = SCALE_OPTIONS.find((option) => option.major === note)?.scale;
                      if (nextScale) {
                        onUpdateConfig({ scale: nextScale }, true);
                      }
                    }}
                    className={cn(
                      "px-2 py-1.5 rounded text-xs font-mono transition-all",
                      currentNote === note
                        ? "bg-white/15 text-foreground"
                        : "bg-white/5 text-muted-foreground hover:bg-white/10"
                    )}
                  >
                    {note}
                  </button>
                ))}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
});
